import { Table, useOnScrollDown } from 'redesign';
import { DEFAULT_PAGE_SIZE } from 'shared/utils/constants';
import { RenderUnsettledUI } from 'shared/components';
import { Box } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useOgpCompanyTypeContext } from 'ogp/limited-company-capabilities';
import {
  INITIAL_PAGE,
  TableShiftPlanningColumnDefinition,
  TableShiftPlanningOptions,
  TableShiftPlanningProps,
} from './shift-planning-table.types';

import { useGetTableShiftPlanningShifts } from './hooks/use-get-table-shift-planning-shifts';
import { getTableShiftPlanningData } from './utils/get-table-shift-planning-data';
import { getShiftPlanningColumnDefinition } from './utils/get-table-shift-planning-column-definition';
import { getSelectedRows } from './utils/get-selected-row-ids';
import { getMultiDayShiftRowGroupClass } from './helpers/get-multi-day-shift-row-group-class';
import { usePlanningParamsContext } from '../../providers/planning-params-provider';
import { usePlanningContext } from '../../providers/planning-actions-provider';

export const TableShiftPlanningProvider = () => {
  const { filters } = usePlanningParamsContext();

  const shiftsQuery = useGetTableShiftPlanningShifts({
    fromDate: filters.fromDate?.toFormat('yyyy-MM-dd') ?? DateTime.now().toFormat('yyyy-MM-dd'),
    toDate: filters.toDate?.toFormat('yyyy-MM-dd'),
    company: filters.company,
    branchOffices: filters.branchOfficeIds ?? undefined,
    departments: filters.departmentIds ?? undefined,
    pageParam: INITIAL_PAGE,
    pageSizeParam: DEFAULT_PAGE_SIZE,
    jobType: filters.jobType,
    hasApplicants: filters.hasApplicants,
    filledStatus: filters.filledStatus,
    isFreelance: filters.isFreelance,
    isTempWork: filters.isTempWork,
    isEmployee: filters.isEmployee,
    search: filters.search,
  });

  if (shiftsQuery.status !== 'success') {
    return <RenderUnsettledUI data={shiftsQuery} />;
  }

  return <ShiftPlanningTable shiftsQuery={shiftsQuery} />;
};

const ShiftPlanningTable = ({ shiftsQuery }: TableShiftPlanningProps) => {
  const { t } = useTranslation('ogp-screen-specific');
  const { isLimitedCompany } = useOgpCompanyTypeContext();
  const { selectedShiftIds, handleSelectionChange } = usePlanningContext();
  const flatData = useMemo(
    () => shiftsQuery.data.pages.flatMap((page) => page.items),
    [shiftsQuery.data]
  );
  const tableData = useMemo(() => getTableShiftPlanningData(flatData), [flatData]);
  const columns = useMemo(
    () => getShiftPlanningColumnDefinition(t, { isLimitedCompany }),
    [isLimitedCompany, t]
  );
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: shiftsQuery,
    flatData: flatData,
  });
  const { setFilters } = usePlanningParamsContext();
  const selectedRows = getSelectedRows(flatData, selectedShiftIds);
  const multiDayShiftRowGroupClasses = useMemo(
    () => getMultiDayShiftRowGroupClass(tableData),
    [tableData]
  );
  const rowPropGetter = useCallback(
    (_props: unknown, { row }: { row: { original: TableShiftPlanningColumnDefinition } }) => {
      const rowGroupClass = multiDayShiftRowGroupClasses[row.original.id];
      if (rowGroupClass == null || rowGroupClass.length === 0) {
        return {};
      }

      return { className: rowGroupClass };
    },
    [multiDayShiftRowGroupClasses]
  );

  const onSelectionChange = useCallback(
    (rows: TableShiftPlanningColumnDefinition[]) => {
      const selectedIds = rows.map((row) => row.id);
      handleSelectionChange(selectedIds);
    },
    [handleSelectionChange]
  );

  return (
    <Box minHeight={0}>
      <Table<TableShiftPlanningColumnDefinition, TableShiftPlanningOptions>
        stickyHeader={true}
        columns={columns}
        data={tableData}
        tableStyle={{ tableLayout: 'fixed' }}
        onScroll={fetchIfBottomScrolled}
        onShowDetail={(shiftId: string) => {
          setFilters({ shiftId });
        }}
        data-testid="shift-planning-virtual-table"
        onSelectionChange={isLimitedCompany ? undefined : onSelectionChange}
        selectable={!isLimitedCompany}
        selectedRows={isLimitedCompany ? undefined : selectedRows}
        rowPropGetter={rowPropGetter}
      />
    </Box>
  );
};
